// Мой аккаунт в шапке, как у Google: аватар (или буква в кружке) с точкой соединения; клик — имя, почта,
// роль, связь и время синхронизации, служебные разделы (история, настройки) и выход. Свой кружок из списка
// присутствия ушёл сюда — он и есть «я здесь». Имя и картинка — те же, что уходят в присутствие (self в сторе).
import { useCallback, useRef, useState, type ComponentType } from 'react';
import { NavLink } from 'react-router';
import { LogOut } from 'lucide-react';
import { syncedLabel } from '../people';
import { useAuth } from '../auth/store';
import { ROLE_LABEL, type Role } from '../auth/roles';
import { useData } from '../store';
import { Avatar } from './Avatar';
import { CONNECTION } from './Connection';
import { cn } from './cn';
import { useDismiss } from './dismiss';
import { Tooltip } from './Tooltip';

type ToolSection = { path: string; label: string; icon: ComponentType<{ className?: string }> };

export function AccountMenu({ tools = [] }: { tools?: ToolSection[] }) {
  const { profile, signOut } = useAuth();
  const connection = useData((s) => s.connection);
  const syncedAt = useData((s) => s.syncedAt);
  const self = useData((s) => s.self);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  // закрыли меню — фокус уходит с кружка, иначе после Escape на нём остаётся кольцо focus-visible
  const close = useCallback(() => { setOpen(false); ref.current?.querySelector('button')?.blur(); }, []);
  useDismiss(ref, open, close);
  if (!profile || !self) return null;
  const { name, color, avatar } = self;
  const { text, dot } = CONNECTION[connection];
  const synced = syncedLabel(syncedAt);
  return (
    <span ref={ref} className="relative inline-flex" data-presence={profile.id} data-account-menu>
      <Tooltip title={name} lines={[`You · ${text}`, { text: synced, tone: 'muted' }]} side="bottom" disabled={open}>
      <button
        type="button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}
        className="min-h-0 rounded-full focus:outline-none focus-visible:ring-4 ring-accent/70" aria-label="Account"
      >
        <Avatar name={name} color={color} src={avatar} size={32} className="ring-2 ring-white">
          <span className={cn('absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ring-2 ring-white', dot)} role="status" aria-label={text} />
        </Avatar>
      </button>
      </Tooltip>
      {open && (
        <div role="menu" className="absolute right-0 top-full mt-2 z-50 w-72 bg-white border border-gray-300 rounded-xl shadow-lg p-3 text-sm">
          <div className="flex items-center gap-3 px-1 pb-3 border-b border-gray-200">
            <Avatar name={name} color={color} src={avatar} size={40} />
            <span className="min-w-0">
              <span className="block font-semibold truncate">{name}</span>
              <span className="block text-gray-600 truncate">{profile.email}</span>
              <span className="block text-xs text-gray-500">{ROLE_LABEL[profile.role as Role] ?? profile.role} · <span className={cn('inline-block w-2 h-2 rounded-full align-middle mr-1', dot)} aria-hidden />{text} · {synced}</span>
            </span>
          </div>
          {tools.length > 0 && (
            <div className="py-2 border-b border-gray-200">
              {tools.map((t) => (
                <NavLink key={t.path} to={t.path} role="menuitem" onClick={() => setOpen(false)}
                  className={({ isActive }) => cn('min-h-0 w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-gray-100', isActive ? 'text-blue-600 font-medium' : 'text-gray-800')}>
                  <t.icon className="w-4 h-4" /> {t.label}
                </NavLink>
              ))}
            </div>
          )}
          <button type="button" role="menuitem" onClick={() => void signOut()} className="min-h-0 mt-2 w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-gray-100 text-gray-800 font-medium">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      )}
    </span>
  );
}
